import { useMemo, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Modal } from "../../../components/core/basic/Modal";
import { Button } from "../../../components/core/basic/Button";
import { Select } from "../../../components/fields/base/Select";
import { BID_STRATEGY_OPTIONS } from "../../../components/core/blocks/AdPlan/constants";
import { useBulkUpdateAdPropertiesV2 } from "../api/useBulkUpdateAdPropertiesV2";
import { useResolvedLabels } from "../../../i18n/useResolvedLabels";

interface BulkBidStrategyModalProps {
  advertisementIds: number[];
}

type BidStrategyValue = "manual" | "LEGACY_FOR_SALES" | "AUTO_FOR_SALES";

const bidStrategyOptions = [BID_STRATEGY_OPTIONS.DOWN_ONLY, BID_STRATEGY_OPTIONS.UP_DOWN, BID_STRATEGY_OPTIONS.FIXED];

export const BulkBidStrategyModal = NiceModal.create(({ advertisementIds }: BulkBidStrategyModalProps) => {
  const { t } = useTranslation(["advertising", "common"]);
  const modal = useModal();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();
  const [bidStrategy, setBidStrategy] = useState<BidStrategyValue>(BID_STRATEGY_OPTIONS.DOWN_ONLY.value);
  const resolvedBidStrategyOptions = useResolvedLabels(bidStrategyOptions, ["advertising"]);

  const selectedBidStrategy = useMemo(
    () => resolvedBidStrategyOptions.find((option) => option.value === bidStrategy),
    [bidStrategy, resolvedBidStrategyOptions],
  );

  const handleConfirm = () => {
    bulkUpdatePropertiesMutation.mutate(
      {
        advertisements: advertisementIds,
        updates: [
          {
            field: "bidding_strategy",
            action: "set",
            value: bidStrategy,
          },
        ],
      },
      {
        onSuccess: () => modal.remove(),
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => !isOpen && modal.remove()}
      title={t(($) => $.advertising["Update Bidding Strategy"])}
      css={{ minWidth: "40vw" }}
    >
      <VStack gap="large" alignItems="stretch" paddingY="xlarge" paddingX="xlarge">
        <p className={css({ textAlign: "center" })}>
          {t(
            ($) =>
              $.advertising[
                "Please note that only Sponsored Product and Sponsored Product Auto media plans will be updated."
              ],
          )}
        </p>
        <HStack justifyContent={"center"}>
          <Select
            label={t(($) => $.advertising["Bid Strategy"])}
            selectedKey={bidStrategy}
            onSelectionChange={(value) => setBidStrategy(value as BidStrategyValue)}
            items={resolvedBidStrategyOptions}
            css={{ maxWidth: "250px" }}
          />
        </HStack>
        <p className={css({ textAlign: "center" })}>
          {t(
            ($) =>
              $.advertising[
                "Are you sure you want to update the bidding strategy to {strategy} for the selected media plans?"
              ],
            { strategy: selectedBidStrategy?.label ?? "", count: advertisementIds.length },
          )}
        </p>
        <HStack justifyContent="space-around">
          <Button variant="secondary" onPress={() => modal.remove()}>
            {t(($) => $.common.actions["Cancel"])}
          </Button>
          <Button onPress={handleConfirm} isPending={bulkUpdatePropertiesMutation.isPending} isDisabled={!bidStrategy}>
            {t(($) => $.advertising["Confirm"])}
          </Button>
        </HStack>
      </VStack>
    </Modal>
  );
});
